import { Box } from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import styled from '@emotion/styled/macro'
import theme from 'theme'

const INPUT_WIDTH = 240

export const DatePickerWrapper = styled(Box)`
  .date-picker-input {
    min-width: ${INPUT_WIDTH}px;
    cursor: pointer;
    border-radius: 12px;
    border-width: 1px;

    .MuiInputLabel-root {
      color: ${theme.colors.dropdownLabel};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 25px;
      margin-left: 4px;
    }

    & .Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.dropdownBorder};
    }

    .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.dropdownBorder};
      border-radius: 12px;
      border-width: 1px;
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.cta};
      border-width: 1px;
    }

    &.Mui-focused.MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.dropdownBorder};
      border-width: 1px;
    }

    &.date-picker-input--open .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.cta};
      border-width: 1px;
    }

    .MuiInputAdornment-root {
      margin-right: 4px;

      svg path {
        stroke: ${theme.colors.legacyNeutral};
      }
    }

    .MuiOutlinedInput-input {
      color: ${theme.colors.legacyNeutral};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 20px;
      cursor: pointer;
      padding: 14px 16px 14px 8px;
    }
  }
`

export const StyledDatePickerWrapper = styled(Box)`
  background-color: ${theme.colors.legacy2d2d3b};
  border-radius: 12px;
  padding: 16px;
  min-width: ${INPUT_WIDTH}px;
`

export const ActionButtons = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;

  .cancel-button {
    color: ${theme.colors.menuItemText};
    text-transform: none;
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;

    &:hover {
      background-color: ${theme.colors.calendarActionHover};
    }
  }

  .ok-button {
    color: ${theme.colors.cta};
    text-transform: none;
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;

    &:hover {
      background: ${theme.baseColors.primary[70]};
      color: ${theme.baseColors.primary[20]};
    }
  }
`

export const StyledDateCalendar = styled(DateCalendar)`
  background-color: transparent;
  color: ${theme.colors.foreground};

  .MuiPickersYear-root {
    .MuiPickersYear-yearButton {
      &:hover {
        background-color: ${theme.colors.calendarActionHover};
      }
    }

    .Mui-selected {
      background-color: ${theme.colors.cta};
      color: ${theme.colors.legacySurfaceContainerBackground4};
      font-weight: 500;

      &:hover,
      &:focus {
        background: ${theme.colors.legacyPrimary70};
      }
    }
  }

  & .MuiPickersCalendarHeader-root {
    padding-left: 24px;
    padding-right: 8px;

    & .MuiPickersCalendarHeader-label {
      color: ${theme.colors.menuItemText};
      font-family: Quicksand, sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
    }

    & .MuiButtonBase-root.MuiPickersCalendarHeader-switchViewButton {
      .MuiPickersCalendarHeader-switchViewIcon {
        path {
          fill: ${theme.colors.menuItemText};
        }
      }
    }
  }

  & .MuiDayCalendar-header {
    padding-left: 24px;
    padding-right: 24px;

    & .MuiDayCalendar-weekDayLabel {
      color: ${theme.colors.caption};
      text-align: center;
      font-family: Quicksand, sans-serif;
      font-size: 12px;
      font-style: normal;
      font-weight: 600;
      line-height: 150%;
      width: 36px !important;
      min-width: 36px !important;
      height: 40px !important;
    }
  }

  & .MuiDayCalendar-monthContainer {
    padding-left: 16px;
    padding-right: 16px;
  }

  & .MuiPickersDay-root {
    width: 36px;
    height: 36px;
    color: ${theme.colors.calendarDay};
    border-radius: 8px;
    text-align: center;
    font-family: Quicksand, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;

    &:hover {
      background-color: ${theme.colors.calendarActionHover};
    }

    &.Mui-disabled {
      color: ${theme.colors.calendarDisabledDay} !important;
      text-align: center;
      font-family: Quicksand, sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
      opacity: 1;
    }

    &.MuiPickersDay-today {
      border: 1px solid rgba(255, 255, 255, 0.3);
      background-color: transparent;

      html[data-theme='light'] & {
        border-color: ${theme.colors.dropdownLabel};
      }

      &:hover {
        background-color: ${theme.colors.calendarActionHover};
      }

      &.Mui-selected {
        border: none;
      }
    }

    &.Mui-selected {
      background: ${theme.colors.cta};
      color: ${theme.colors.legacySurfaceContainerBackground4};
      font-weight: 500;

      &:focus,
      &:active {
        background: ${theme.colors.cta};
      }

      &:hover {
        background: ${theme.colors.legacyPrimary70};
      }
    }
  }

  & .MuiPickersArrowSwitcher-root {
    & .MuiIconButton-root {
      color: ${theme.colors.calendarIcon};

      &:hover {
        background-color: ${theme.colors.calendarActionHover};
      }

      &.Mui-disabled {
        color: rgba(255, 255, 255, 0.3);
      }
    }
  }
`
